// Brand constants & content for Insurances Solutions Agency, LLC
import {
  Heart,
  Stethoscope,
  ShieldCheck,
  Car,
  Home as HomeIcon,
  Building2,
  KeyRound,
  Cross,
  PiggyBank,
  LineChart,
  ClipboardCheck,
  Briefcase,
} from "lucide-react";

export const BRAND = {
  name: "Insurances Solutions Agency, LLC",
  shortName: "ISA",
  tagline: "Protecting What Matters Most",
  email: "insurancessolutionsagency@gmail.com",
  phone: "229-733-3500",
  phoneTel: "+12297333500",
  facebook: "https://www.facebook.com/share/1EozWP9rDi/?mibextid=wwXIfr",
  address: "628 Pine Ave, Albany, GA",
  addressMapUrl:
    "https://www.google.com/maps/search/?api=1&query=628+Pine+Ave+Albany+GA",
  yearsOfExperience: "",
  logoHorizontal:
    "https://customer-assets.emergentagent.com/job_isa-luxury-preview/artifacts/l3zqsitr_IMG_9264.jpeg",
  logoShield:
    "https://customer-assets.emergentagent.com/job_isa-luxury-preview/artifacts/kdwy2lvd_8200EB1C-11AC-48C9-BAC6-A509B4C9AEF3.png",
  images: {
    heroBg:
      "https://static.prod-images.emergentagent.com/jobs/ec0e220a-3b59-4f94-a568-4c2f1f9278ef/images/7d42705457075b5639b6362c31eb832ea71418b11dbbf5e10a7b8dca59f07f1d.png",
    shieldPattern:
      "https://static.prod-images.emergentagent.com/jobs/ec0e220a-3b59-4f94-a568-4c2f1f9278ef/images/6d45cef3a66d7096f512883c3df4cbe3695ef1419fb6bfc7114436e7b763680f.png",
    servicesTexture:
      "https://static.prod-images.emergentagent.com/jobs/ec0e220a-3b59-4f94-a568-4c2f1f9278ef/images/21ac0dfc07b404b74c449dae6e4ab8f12889185901533df9e7860ec56173c7a3.png",
    statsBg:
      "https://static.prod-images.emergentagent.com/jobs/ec0e220a-3b59-4f94-a568-4c2f1f9278ef/images/b741861a512d088dd5c7b477bd8cb3bbbb8a0b987445f70a6f1df1fb4c88b2f3.png",
    architecture:
      "https://images.unsplash.com/photo-1490093158370-1a6be674437b?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
};

export const NAV_LINKS = [
  { label: "Home", path: "/" },
  { label: "About", path: "/about" },
  { label: "Services", path: "/services" },
  { label: "FAQ", path: "/faq" },
  { label: "Contact", path: "/contact" },
];

export const SERVICES = [
  {
    slug: "life",
    title: "Life Insurance",
    short: "Protect the people who depend on you with flexible term and whole life policies.",
    description:
      "Term, whole, and universal life policies designed to safeguard your family's financial future. We compare carriers to match the right coverage to your goals.",
    icon: Heart,
  },
  {
    slug: "health",
    title: "Health Insurance",
    short: "Individual and family plans with the coverage you need, at a price that fits.",
    description:
      "Marketplace and private plans for individuals, families, and the self-employed. We help you understand benefits, networks, and out-of-pocket costs.",
    icon: Stethoscope,
  },
  {
    slug: "medicare",
    title: "Medicare Supplements",
    short: "Confidently navigate Medicare with personalized plan guidance.",
    description:
      "Medicare Supplement, Advantage, and Part D plans explained clearly. We help you compare options so you keep your doctors and your peace of mind.",
    icon: ShieldCheck,
  },
  {
    slug: "auto",
    title: "Auto Insurance",
    short: "Smart coverage for every vehicle, every driver, every road.",
    description:
      "Liability, collision, comprehensive, and bundled multi-vehicle coverage with trusted national carriers. Real protection at a real value.",
    icon: Car,
  },
  {
    slug: "home",
    title: "Homeowners Insurance",
    short: "Defend your home, your belongings, and your equity.",
    description:
      "Property coverage tailored to your home's value, location, and risk profile, including options for valuables, water backup, and additional structures.",
    icon: HomeIcon,
  },
  {
    slug: "renters",
    title: "Renters Insurance",
    short: "Affordable protection for everything you own, even if you don't own the walls.",
    description:
      "Coverage for personal property, liability, and loss of use, often at less than the cost of a streaming subscription.",
    icon: KeyRound,
  },
  {
    slug: "commercial",
    title: "Commercial & Small Business",
    short: "Coverage built for owners who built it themselves.",
    description:
      "General liability, property, professional liability, BOP packages, workers' compensation, and commercial auto for growing businesses.",
    icon: Building2,
  },
  {
    slug: "final-expense",
    title: "Final Expense Insurance",
    short: "A dignified plan that takes the financial burden off your loved ones.",
    description:
      "Whole life policies designed to cover funeral costs, medical bills, and end-of-life expenses, with simple qualification and lifetime guarantees.",
    icon: Cross,
  },
  {
    slug: "retirement",
    title: "Retirement & Financial Solutions",
    short: "Plan today for the retirement you've imagined.",
    description:
      "Retirement-focused strategies including IRAs, rollovers, and tax-advantaged income planning aligned to your timeline and risk preference.",
    icon: PiggyBank,
  },
  {
    slug: "annuities",
    title: "Annuities",
    short: "Create predictable, lifetime income you can count on.",
    description:
      "Fixed, indexed, and immediate annuity options designed to protect principal, defer taxes, and provide guaranteed income for life.",
    icon: LineChart,
  },
  {
    slug: "reviews",
    title: "Policy Reviews & Free Quotes",
    short: "A complimentary, no-pressure review of your current coverage.",
    description:
      "We review what you already have, identify gaps and overlaps, and provide free quote comparisons across top-rated carriers.",
    icon: ClipboardCheck,
  },
  {
    slug: "business",
    title: "Property & Casualty",
    short: "Comprehensive P&C solutions for individuals and businesses.",
    description:
      "Bundle home, auto, umbrella, and specialty coverage with carriers known for fast claims service and consistent renewals.",
    icon: Briefcase,
  },
];

export const STATS = [
  { value: "Free", label: "Quotes & Policy Reviews" },
  { value: "15+", label: "Lines of Coverage" },
  { value: "A-Rated", label: "Carrier Partners" },
  { value: "100%", label: "Personalized Guidance" },
];

export const VALUES = [
  {
    title: "Integrity",
    body: "Straightforward advice, transparent options, and no pressure — ever.",
  },
  {
    title: "Relationships",
    body: "You're not a policy number. We're here for life's changes, not just renewals.",
  },
  {
    title: "Professionalism",
    body: "Licensed, knowledgeable, and accountable — every conversation, every claim.",
  },
  {
    title: "Community",
    body: "Built on decades of serving our neighbors with care and consistency.",
  },
];

export const TESTIMONIALS = [
  {
    initials: "JM",
    name: "Janet M.",
    role: "Homeowner",
    quote:
      "They reviewed our existing policies, found two coverage gaps we didn't know we had, and saved us nearly $40 a month. Honest, kind, and incredibly knowledgeable.",
  },
  {
    initials: "RC",
    name: "Robert C.",
    role: "Small Business Owner",
    quote:
      "I shopped four agencies before landing here. Nobody else took the time to actually understand my business. The commercial package they built is exactly what I needed.",
  },
  {
    initials: "DL",
    name: "Denise L.",
    role: "Retiree",
    quote:
      "Medicare was overwhelming until I sat down with this team. They walked me through every option without rushing me. I finally feel like I understand my coverage.",
  },
  {
    initials: "AS",
    name: "Andre S.",
    role: "Father of Two",
    quote:
      "We finally have life insurance that fits our budget — and an advisor who actually answers her phone. Highly recommend for any young family.",
  },
];

export const FAQS = [
  {
    q: "How do I know I'm getting the right coverage?",
    a: "We start with a free, no-obligation policy review. We listen to your needs, evaluate what you currently have, then compare options from multiple top-rated carriers — so you can choose with confidence, not pressure.",
  },
  {
    q: "Is requesting a quote really free?",
    a: "Yes. Quotes and policy reviews are always complimentary. You're under no obligation to purchase, and we never share your information with anyone outside our agency.",
  },
  {
    q: "Which insurance carriers do you represent?",
    a: "We're independent, which means we work with multiple A-rated national and regional carriers across life, health, Medicare, auto, home, renters, and commercial lines. We shop the market for you.",
  },
  {
    q: "Can you help me with Medicare even if I'm new to it?",
    a: "Absolutely. We help with initial enrollment, Medicare Supplement plans, Advantage plans, and Part D drug coverage. We explain each option in plain language and review your plan annually.",
  },
  {
    q: "Do you offer retirement and annuity planning?",
    a: "Yes. We provide retirement-focused financial products including fixed and indexed annuities, IRAs, and rollover options designed for long-term income stability and tax-advantaged growth.",
  },
  {
    q: "What information do I need to get started?",
    a: "Just the basics — your name, contact info, and the type of coverage you'd like a quote on. For more detailed quotes, we may ask for your date of birth, ZIP code, and current policy declarations (if any).",
  },
  {
    q: "How quickly will I hear back after submitting a form?",
    a: "We respond to most inquiries within one business day, and often the same day. For urgent matters, please call us directly at 229-733-3500.",
  },
  {
    q: "Can you review my existing policies even if I bought them elsewhere?",
    a: "Of course. Policy reviews are one of our most valuable free services. We'll identify gaps, overlaps, and potential savings — no obligation to switch.",
  },
];

export const INSURANCE_TYPES = [
  "Life Insurance",
  "Health Insurance",
  "Medicare Supplement",
  "Auto Insurance",
  "Homeowners Insurance",
  "Renters Insurance",
  "Commercial / Small Business",
  "Final Expense",
  "Retirement / Annuities",
  "Policy Review",
  "Other",
];

export const CONSULTATION_TOPICS = [
  "General Insurance Review",
  "Life Insurance Planning",
  "Medicare Guidance",
  "Retirement & Annuities",
  "Small Business Coverage",
  "Home & Auto Bundle",
  "Other",
];

export const TIME_PREFERENCES = [
  { value: "morning", label: "Morning (8am - 12pm)" },
  { value: "afternoon", label: "Afternoon (12pm - 5pm)" },
  { value: "evening", label: "Evening (5pm - 7pm)" },
];
